import { apiFetch } from "./client";
import type {
  AcceptInviteRequest,
  AcceptInviteResult,
  ClassScheduleDto,
  ClassSessionsResponse,
  CourtDto,
  InviteValidationDto,
  MeTenantsResponse,
  PagedResult,
  SportDto,
  TenantDto,
} from "../../types";

/**
 * Prefixos por grupo de rota da apogeu-api.
 * Cada grupo declara sua versão de forma independente — migrar um grupo para
 * `/v2` significa trocar o prefixo dele aqui, sem afetar os demais.
 */
export const GROUPS = {
  ME: "/api/me",
  TENANTS: "/api/tenants",
  INVITES: "/api/invites",
  PUBLIC_INVITES: "/api/public/invites",
  CLASSES: "/api/v1/classes",
  CLASS_SESSIONS: "/api/v1/class-sessions",
  COURTS: "/api/v1/courts",
  SPORTS: "/api/v1/sports",
} as const;

const CATALOG_PAGE_SIZE = "100";

export async function fetchMyTenants(token: string): Promise<MeTenantsResponse> {
  return apiFetch<MeTenantsResponse>(`${GROUPS.ME}/tenants`, { token });
}

export async function switchTenant(token: string, tenantId: string): Promise<void> {
  await apiFetch<unknown>(`${GROUPS.TENANTS}/${tenantId}/switch`, { token, method: "POST" });
}

export async function fetchInviteValidation(code: string): Promise<InviteValidationDto> {
  return apiFetch<InviteValidationDto>(`${GROUPS.PUBLIC_INVITES}/${encodeURIComponent(code)}`);
}

export async function acceptInvite(
  token: string,
  code: string,
  payload: AcceptInviteRequest = {},
): Promise<AcceptInviteResult> {
  return apiFetch<AcceptInviteResult>(`${GROUPS.INVITES}/${encodeURIComponent(code)}/accept`, {
    token,
    method: "POST",
    body: payload,
  });
}

/** Dados do CT ativo, incluindo o `timeZoneId` usado para montar a semana. */
export async function fetchTenant(token: string, tenantId: string, signal?: AbortSignal): Promise<TenantDto> {
  return apiFetch<TenantDto>(`${GROUPS.TENANTS}/${tenantId}`, { token, signal });
}

export interface ClassScheduleQuery {
  weekStart: string;
  courtId: string;
  signal?: AbortSignal;
}

export async function fetchClassSchedule(
  token: string,
  { weekStart, courtId, signal }: ClassScheduleQuery,
): Promise<ClassScheduleDto> {
  const query = new URLSearchParams({ weekStart, courtId });
  return apiFetch<ClassScheduleDto>(`${GROUPS.CLASSES}/schedule?${query}`, { token, signal });
}

export interface ClassSessionsQuery {
  startDate: string;
  endDate: string;
  courtId?: string;
  signal?: AbortSignal;
}

export async function listClassSessions(
  token: string,
  { startDate, endDate, courtId, signal }: ClassSessionsQuery,
): Promise<ClassSessionsResponse> {
  const query = new URLSearchParams({ startDate, endDate });
  if (courtId) query.set("courtId", courtId);
  return apiFetch<ClassSessionsResponse>(`${GROUPS.CLASS_SESSIONS}?${query}`, { token, signal });
}

export async function listCourts(token: string, signal?: AbortSignal): Promise<PagedResult<CourtDto>> {
  const query = new URLSearchParams({ pageSize: CATALOG_PAGE_SIZE, isActive: "true" });
  return apiFetch<PagedResult<CourtDto>>(`${GROUPS.COURTS}?${query}`, { token, signal });
}

export async function listSports(token: string, signal?: AbortSignal): Promise<PagedResult<SportDto>> {
  const query = new URLSearchParams({ pageSize: CATALOG_PAGE_SIZE, isActive: "true" });
  return apiFetch<PagedResult<SportDto>>(`${GROUPS.SPORTS}?${query}`, { token, signal });
}
